import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import LangInput from "@/components/LangInput";
import {
  Drop, Plant, Plus, PencilSimple, Trash, Sparkle, UploadSimple,
  Check, X, Warning, ChartLine,
} from "@phosphor-icons/react";

/**
 * SoilWaterPanel — inline dashboard on FarmersFarmDetail (spec §14, §15).
 *
 * Two side-by-side tabs: Soil Pulse + Water Pulse. Owner-only forms plus
 * an "Upload lab PDF" button that calls the AI extraction endpoint and
 * pre-fills the form (requires user confirmation before saving).
 */
export default function SoilWaterPanel({ farmId, isOwner }) {
  const [tab, setTab] = useState("soil");
  return (
    <section className="mt-8" data-testid="farm-soil-water-panel">
      <div className="flex items-center gap-3 flex-wrap">
        <h2 className="font-serif-display text-3xl md:text-4xl leading-tight">Soil & Water</h2>
        <div className="ml-auto inline-flex items-center gap-1 rounded-full border border-black/15 bg-white p-1">
          <TabBtn active={tab === "soil"} onClick={() => setTab("soil")} testid="soil-tab">
            <Plant size={12} weight="fill" /> Soil
          </TabBtn>
          <TabBtn active={tab === "water"} onClick={() => setTab("water")} testid="water-tab">
            <Drop size={12} weight="fill" /> Water
          </TabBtn>
        </div>
      </div>
      {tab === "soil" ? <SoilPanel farmId={farmId} isOwner={isOwner} /> : <WaterPanel farmId={farmId} isOwner={isOwner} />}
    </section>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase transition ${
        active ? "bg-[#241209] text-[#F5EBDC]" : "text-[#241209] hover:bg-[#FBF2E1]"
      }`}
    >{children}</button>
  );
}


// =====================================================================
//  SOIL
// =====================================================================
const SOIL_EMPTY = {
  record_date: "", soil_type: "", texture: "", ph: "",
  moisture_pct: "", temperature_c: "", organic_matter_pct: "",
  nitrogen: "", phosphorus: "", potassium: "", salinity_ec: "",
  drainage: "", unit: "ppm", lab_name: "", notes: "", privacy: "private",
};

function SoilPanel({ farmId, isOwner }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(SOIL_EMPTY);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [aiExtracted, setAiExtracted] = useState(false);

  const load = () => {
    setLoading(true);
    http.get(`/farmers/farms/${farmId}/soil`)
      .then(({ data }) => setRecords(data.records || []))
      .catch(() => setRecords([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [farmId]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => {
          if (v === "" || v === null || v === undefined) return [k, undefined];
          if (["ph", "moisture_pct", "temperature_c", "organic_matter_pct",
               "nitrogen", "phosphorus", "potassium", "salinity_ec"].includes(k)) {
            const n = parseFloat(v);
            return [k, Number.isFinite(n) ? n : undefined];
          }
          return [k, v];
        }),
      );
      payload.ai_extracted = aiExtracted;
      await http.post(`/farmers/farms/${farmId}/soil`, payload);
      toast.success(aiExtracted ? "AI-extracted values saved" : "Soil record saved");
      setForm(SOIL_EMPTY);
      setAiExtracted(false);
      setShowForm(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const remove = async (sid) => {
    if (!window.confirm("Delete this soil record?")) return;
    try {
      await http.delete(`/farmers/soil/${sid}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const extractPdf = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") return toast.error("Please upload a PDF file.");
    if (file.size > 8 * 1024 * 1024) return toast.error("PDF must be under 8 MB.");
    setExtracting(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise((res, rej) => {
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      const { data } = await http.post(`/farmers/farms/${farmId}/soil/extract-pdf`, {
        pdf_base64: String(dataUrl),
        filename: file.name,
      });
      // Prefill the form with the AI values.
      const ex = data.extracted || {};
      setForm((cur) => ({
        ...cur,
        record_date: ex.record_date || cur.record_date,
        lab_name: ex.lab_name || cur.lab_name,
        soil_type: ex.soil_type || cur.soil_type,
        texture: ex.texture || cur.texture,
        ph: ex.ph ?? cur.ph,
        moisture_pct: ex.moisture_pct ?? cur.moisture_pct,
        organic_matter_pct: ex.organic_matter_pct ?? cur.organic_matter_pct,
        nitrogen: ex.nitrogen ?? cur.nitrogen,
        phosphorus: ex.phosphorus ?? cur.phosphorus,
        potassium: ex.potassium ?? cur.potassium,
        salinity_ec: ex.salinity_ec ?? cur.salinity_ec,
        drainage: ex.drainage || cur.drainage,
        unit: ex.unit || cur.unit,
        notes: ex.notes || cur.notes,
      }));
      setAiExtracted(true);
      setShowForm(true);
      toast.success("AI extracted values — please confirm before saving");
      // Give React a tick to mount the form, then scroll it into view.
      setTimeout(() => {
        const el = document.querySelector('[data-testid="soil-form"]');
        if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 120);
    } catch (e) { toast.error(e?.response?.data?.detail || "AI extraction failed"); }
    finally { setExtracting(false); }
  };

  return (
    <div className="mt-4" data-testid="soil-panel">
      {/* Owner controls */}
      {isOwner && (
        <div className="rounded-2xl border border-black/10 bg-white p-4 flex flex-wrap items-center gap-2" data-testid="soil-controls">
          <button
            onClick={() => { setShowForm(!showForm); setAiExtracted(false); }}
            data-testid="soil-add-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
          >
            <Plus size={11} weight="bold" /> {showForm ? "Cancel" : "Add soil record"}
          </button>
          <label className="inline-flex items-center gap-1.5 rounded-full border border-black/20 bg-white px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5 cursor-pointer" data-testid="soil-upload-pdf">
            <UploadSimple size={11} weight="bold" /> {extracting ? "Extracting…" : "Upload lab PDF"}
            <input type="file" accept="application/pdf" className="hidden" disabled={extracting} onChange={(e) => extractPdf(e.target.files?.[0])} />
          </label>
          <div className="ml-auto text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830]">
            AI extract uses Gemini vision · confirm before saving
          </div>
        </div>
      )}

      {/* Form */}
      {isOwner && showForm && (
        <div className="mt-3 rounded-2xl border border-black/10 bg-white p-5" data-testid="soil-form">
          {aiExtracted && (
            <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 p-3 text-xs flex items-start gap-2" data-testid="soil-ai-banner">
              <Sparkle size={14} weight="fill" className="mt-0.5" />
              <span>These values were AI-extracted from your PDF. Please check every number and correct anything wrong before saving.</span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Record date"><input type="date" value={form.record_date} onChange={(e) => setForm({ ...form, record_date: e.target.value })} data-testid="soil-input-date" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="Lab name"><input value={form.lab_name} onChange={(e) => setForm({ ...form, lab_name: e.target.value })} data-testid="soil-input-lab" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="Soil type"><input value={form.soil_type} onChange={(e) => setForm({ ...form, soil_type: e.target.value })} data-testid="soil-input-type" placeholder="Alluvial · laterite …" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="pH"><input type="number" step="0.1" value={form.ph} onChange={(e) => setForm({ ...form, ph: e.target.value })} data-testid="soil-input-ph" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="Organic matter %"><input type="number" step="0.1" value={form.organic_matter_pct} onChange={(e) => setForm({ ...form, organic_matter_pct: e.target.value })} data-testid="soil-input-om" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="Moisture %"><input type="number" step="0.1" value={form.moisture_pct} onChange={(e) => setForm({ ...form, moisture_pct: e.target.value })} data-testid="soil-input-moisture" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="Salinity (EC)"><input type="number" step="0.01" value={form.salinity_ec} onChange={(e) => setForm({ ...form, salinity_ec: e.target.value })} data-testid="soil-input-ec" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3">
            <Field label="Nitrogen"><input type="number" step="0.1" value={form.nitrogen} onChange={(e) => setForm({ ...form, nitrogen: e.target.value })} data-testid="soil-input-n" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="Phosphorus"><input type="number" step="0.1" value={form.phosphorus} onChange={(e) => setForm({ ...form, phosphorus: e.target.value })} data-testid="soil-input-p" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="Potassium"><input type="number" step="0.1" value={form.potassium} onChange={(e) => setForm({ ...form, potassium: e.target.value })} data-testid="soil-input-k" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="Unit">
              <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="soil-input-unit" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm">
                <option value="ppm">ppm</option>
                <option value="kg_ha">kg/ha</option>
                <option value="mg_kg">mg/kg</option>
              </select>
            </Field>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Drainage">
              <select value={form.drainage} onChange={(e) => setForm({ ...form, drainage: e.target.value })} data-testid="soil-input-drainage" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm">
                <option value="">—</option>
                <option value="poor">Poor</option>
                <option value="moderate">Moderate</option>
                <option value="good">Good</option>
              </select>
            </Field>
            <Field label="Privacy">
              <select value={form.privacy} onChange={(e) => setForm({ ...form, privacy: e.target.value })} data-testid="soil-input-privacy" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm">
                <option value="private">Private</option>
                <option value="community">Community</option>
                <option value="public">Public</option>
                <option value="research">Research-use permitted</option>
              </select>
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Notes">
              <LangInput as="textarea" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} testid="soil-input-notes" />
            </Field>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => { setShowForm(false); setForm(SOIL_EMPTY); setAiExtracted(false); }} className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5">Cancel</button>
            <button onClick={submit} disabled={saving} data-testid="soil-submit" className="inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
              <Check size={11} weight="bold" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      {/* Records list */}
      <div className="mt-4 space-y-3" data-testid="soil-list">
        {loading && Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-24 rounded-lg bg-white/60 animate-pulse" />)}
        {!loading && records.length === 0 && (
          <div className="rounded-xl border border-dashed border-black/20 p-8 text-center bg-white/60" data-testid="soil-empty">
            <Plant size={20} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
            <div className="font-serif-display text-xl">No soil records yet.</div>
            {isOwner && <p className="text-sm text-[#3A3830] mt-1">Add one manually or upload a lab PDF to extract values.</p>}
          </div>
        )}
        {records.map((r) => (
          <div key={r.id} data-testid={`soil-row-${r.id}`} className="rounded-lg border border-black/10 bg-white p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[220px]">
                <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-2">
                  <Plant size={11} weight="fill" /> {r.record_date} {r.lab_name ? `· ${r.lab_name}` : ""}
                  {r.ai_extracted && <span className="rounded-full bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0 text-[9px]">AI-extracted</span>}
                  {r.privacy && r.privacy !== "private" && <span className="rounded-full bg-white border border-black/15 px-2 py-0 text-[9px] uppercase">{r.privacy}</span>}
                </div>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                  {r.ph !== null && r.ph !== undefined && <MiniStat label="pH" value={r.ph} />}
                  {r.organic_matter_pct !== null && r.organic_matter_pct !== undefined && <MiniStat label="Organic" value={`${r.organic_matter_pct}%`} />}
                  {r.nitrogen !== null && r.nitrogen !== undefined && <MiniStat label="N" value={`${r.nitrogen} ${r.unit || ""}`} />}
                  {r.phosphorus !== null && r.phosphorus !== undefined && <MiniStat label="P" value={`${r.phosphorus} ${r.unit || ""}`} />}
                  {r.potassium !== null && r.potassium !== undefined && <MiniStat label="K" value={`${r.potassium} ${r.unit || ""}`} />}
                  {r.salinity_ec !== null && r.salinity_ec !== undefined && <MiniStat label="EC" value={`${r.salinity_ec} dS/m`} />}
                </div>
                {r.notes && <p className="mt-2 text-xs text-[#3A3830]">{r.notes}</p>}
              </div>
              {isOwner && (
                <button onClick={() => remove(r.id)} data-testid={`soil-delete-${r.id}`} className="rounded-full border border-black/15 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700 p-1">
                  <Trash size={11} weight="fill" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// =====================================================================
//  WATER
// =====================================================================
const WATER_EMPTY = {
  record_date: "", source: "well", well_depth_cm: "", well_static_cm: "",
  rainfall_mm: "", irrigation_mm: "", evapotranspiration_mm: "",
  turbidity: "", quality_notes: "", notes: "", privacy: "private",
};

function WaterPanel({ farmId, isOwner }) {
  const [data, setData] = useState({ records: [], yearly: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(WATER_EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    http.get(`/farmers/farms/${farmId}/water`)
      .then(({ data }) => setData({ records: data.records || [], yearly: data.yearly || [] }))
      .catch(() => setData({ records: [], yearly: [] }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [farmId]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).map(([k, v]) => {
          if (v === "" || v === null || v === undefined) return [k, undefined];
          if (["well_depth_cm", "well_static_cm", "rainfall_mm", "irrigation_mm", "evapotranspiration_mm"].includes(k)) {
            const n = parseFloat(v);
            return [k, Number.isFinite(n) ? n : undefined];
          }
          return [k, v];
        }),
      );
      await http.post(`/farmers/farms/${farmId}/water`, payload);
      toast.success("Water record saved");
      setForm(WATER_EMPTY);
      setShowForm(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const remove = async (wid) => {
    if (!window.confirm("Delete this water record?")) return;
    try {
      await http.delete(`/farmers/water/${wid}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const { records, yearly } = data;

  return (
    <div className="mt-4" data-testid="water-panel">
      {/* Year-over-year strip */}
      {yearly.length > 0 && (
        <div className="rounded-2xl border border-black/10 bg-white p-4" data-testid="water-yearly">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1.5">
            <ChartLine size={12} weight="fill" /> Year-over-year
          </div>
          <div className="mt-3 space-y-4">
            {yearly.slice(0, 3).map((y) => <YearRow key={y.year} y={y} />)}
          </div>
        </div>
      )}

      {/* Owner controls */}
      {isOwner && (
        <div className="mt-3 rounded-2xl border border-black/10 bg-white p-4 flex flex-wrap items-center gap-2" data-testid="water-controls">
          <button
            onClick={() => setShowForm(!showForm)}
            data-testid="water-add-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
          >
            <Plus size={11} weight="bold" /> {showForm ? "Cancel" : "Add water record"}
          </button>
          <div className="ml-auto text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830]">
            Well depth measured from ground surface
          </div>
        </div>
      )}

      {/* Form */}
      {isOwner && showForm && (
        <div className="mt-3 rounded-2xl border border-black/10 bg-white p-5" data-testid="water-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Record date"><input type="date" value={form.record_date} onChange={(e) => setForm({ ...form, record_date: e.target.value })} data-testid="water-input-date" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="Source">
              <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="water-input-source" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm">
                <option value="well">Well</option>
                <option value="canal">Canal</option>
                <option value="rain">Rain gauge</option>
                <option value="pond">Pond</option>
                <option value="river">River</option>
                <option value="reservoir">Reservoir</option>
              </select>
            </Field>
            <Field label="Privacy">
              <select value={form.privacy} onChange={(e) => setForm({ ...form, privacy: e.target.value })} data-testid="water-input-privacy" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm">
                <option value="private">Private</option>
                <option value="community">Community</option>
                <option value="public">Public</option>
                <option value="research">Research-use permitted</option>
              </select>
            </Field>
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Well depth cm"><input type="number" step="1" value={form.well_depth_cm} onChange={(e) => setForm({ ...form, well_depth_cm: e.target.value })} data-testid="water-input-well" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="Rainfall mm"><input type="number" step="0.1" value={form.rainfall_mm} onChange={(e) => setForm({ ...form, rainfall_mm: e.target.value })} data-testid="water-input-rain" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="Irrigation mm"><input type="number" step="0.1" value={form.irrigation_mm} onChange={(e) => setForm({ ...form, irrigation_mm: e.target.value })} data-testid="water-input-irrig" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
            <Field label="ET mm"><input type="number" step="0.1" value={form.evapotranspiration_mm} onChange={(e) => setForm({ ...form, evapotranspiration_mm: e.target.value })} data-testid="water-input-et" className="w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
          </div>
          <div className="mt-3">
            <Field label="Notes">
              <LangInput as="textarea" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} testid="water-input-notes" />
            </Field>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => { setShowForm(false); setForm(WATER_EMPTY); }} className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5">Cancel</button>
            <button onClick={submit} disabled={saving} data-testid="water-submit" className="inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
              <Check size={11} weight="bold" /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}

      {/* Records list */}
      <div className="mt-4 space-y-3" data-testid="water-list">
        {loading && Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-16 rounded-lg bg-white/60 animate-pulse" />)}
        {!loading && records.length === 0 && (
          <div className="rounded-xl border border-dashed border-black/20 p-8 text-center bg-white/60" data-testid="water-empty">
            <Drop size={20} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
            <div className="font-serif-display text-xl">No water records yet.</div>
            {isOwner && <p className="text-sm text-[#3A3830] mt-1">Log rainfall, well depth or irrigation to build a year-over-year picture.</p>}
          </div>
        )}
        {records.map((r) => (
          <div key={r.id} data-testid={`water-row-${r.id}`} className="rounded-lg border border-black/10 bg-white p-4 flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] flex items-center gap-1">
                <Drop size={11} weight="fill" /> {r.record_date} · {r.source || "well"}
                {r.privacy && r.privacy !== "private" && <span className="ml-2 rounded-full bg-white border border-black/15 px-2 py-0 text-[9px] uppercase">{r.privacy}</span>}
              </div>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                {r.well_depth_cm !== null && r.well_depth_cm !== undefined && <MiniStat label="Well cm" value={r.well_depth_cm} />}
                {r.rainfall_mm !== null && r.rainfall_mm !== undefined && <MiniStat label="Rain mm" value={r.rainfall_mm} />}
                {r.irrigation_mm !== null && r.irrigation_mm !== undefined && <MiniStat label="Irrig mm" value={r.irrigation_mm} />}
                {r.evapotranspiration_mm !== null && r.evapotranspiration_mm !== undefined && <MiniStat label="ET mm" value={r.evapotranspiration_mm} />}
              </div>
              {r.notes && <p className="mt-2 text-xs text-[#3A3830]">{r.notes}</p>}
            </div>
            {isOwner && (
              <button onClick={() => remove(r.id)} data-testid={`water-delete-${r.id}`} className="rounded-full border border-black/15 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-700 p-1">
                <Trash size={11} weight="fill" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}


const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const MiniStat = ({ label, value }) => (
  <div className="rounded border border-black/10 bg-[#FBF2E1]/40 px-2 py-1.5">
    <div className="font-mono-tech text-[8px] tracking-[0.24em] uppercase text-[#3A3830]">{label}</div>
    <div className="text-sm font-serif-display">{value}</div>
  </div>
);


const MONTH_LABELS = ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"];

function YearRow({ y }) {
  const months = y.monthly || [];
  const peak = Math.max(1, ...months.map((m) => (m.rainfall_mm || 0) + (m.irrigation_mm || 0)));
  return (
    <div className="rounded-lg border border-black/10 bg-[#FBF2E1]/30 p-4" data-testid={`water-year-${y.year}`}>
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div className="flex items-baseline gap-3">
          <div className="font-serif-display text-2xl">{y.year}</div>
          <div className="text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830]">
            {y.count} entries · avg well {y.avg_well_depth_cm !== null ? `${y.avg_well_depth_cm} cm` : "—"}
          </div>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono-tech tracking-[0.22em] uppercase">
          <span><span className="inline-block w-2 h-2 bg-sky-500 mr-1 rounded-sm" />rain {y.rainfall_mm} mm</span>
          <span><span className="inline-block w-2 h-2 bg-amber-500 mr-1 rounded-sm" />irrig {y.irrigation_mm} mm</span>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-12 gap-1 items-end h-24" data-testid={`water-year-${y.year}-chart`}>
        {months.map((m) => {
          const rain = m.rainfall_mm || 0;
          const irrig = m.irrigation_mm || 0;
          const total = rain + irrig;
          const heightPct = (total / peak) * 100;
          const rainPct = total === 0 ? 0 : (rain / total) * heightPct;
          const irrigPct = total === 0 ? 0 : (irrig / total) * heightPct;
          return (
            <div
              key={m.month}
              className="flex flex-col justify-end items-stretch h-full"
              title={`${MONTH_LABELS[m.month - 1]} · ${rain} mm rain · ${irrig} mm irrig`}
            >
              <div className="flex-1 flex flex-col justify-end rounded-t overflow-hidden bg-[#FBF2E1]/60">
                {irrigPct > 0 && <div className="bg-amber-400" style={{ height: `${irrigPct}%` }} />}
                {rainPct > 0 && <div className="bg-sky-500" style={{ height: `${rainPct}%` }} />}
              </div>
              <div className="text-center mt-1 font-mono-tech text-[8px] tracking-[0.12em] text-[#3A3830]">
                {MONTH_LABELS[m.month - 1]}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
